import type { TournamentStaffDetail } from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { ChevronDown, ChevronUp, Trash2, UserPlus, UserX } from 'lucide-react';
import { useState } from 'react';
import {
  assignExistingPlayer,
  assignNewPlayer,
  draftForNewPlayer,
  draftForPlayer,
  draftsFromTournament,
  moveDraft,
  resultProblems,
  returnToUnclaimed,
  togglePlaced,
  type ResultDraft,
} from '../../results';
import { PlayerPicker } from '../players/PlayerPicker';
import { btnGhost, btnPrimary, Modal } from '../Modal';

/**
 * Recording one tournament's finishing order by hand — the payout sheet, typed in.
 *
 * Every field a result carries is on the row: who, where they are from, what they won, whether it
 * came out of a deal, how many times they bought in, and anything the prize figure does not say
 * on its own. A results sheet is read across, so it is entered across.
 *
 * **Place is the row's position, not a field.** It is shown and not typed, and the way to change
 * one is to move the row. Two rows therefore cannot claim the same place — which the database
 * would otherwise refuse on a unique index, after the save, with nothing pointing at the culprit.
 * The cost is that places are always 1..N with no gaps; a payout sheet does not have gaps.
 *
 * Players are added from one search above the grid rather than a picker per row. A dropdown
 * inside the scrolling grid is clipped by it, and somebody already on the sheet is left out of
 * the results instead of being offered and then refused.
 */
export function UploadResultsModal({
  roomId,
  tournament,
  saving,
  errorMessage,
  onSave,
  onClose,
}: {
  roomId: string;
  /** The tournament's own read: its results, each naming its finisher. */
  tournament: TournamentStaffDetail;
  saving: boolean;
  errorMessage: string | null;
  /** Given the whole sheet: the caller creates any new players, then writes the results. */
  onSave: (rows: ResultDraft[]) => void;
  onClose: () => void;
}) {
  // `entries` is the stored result row count, so this asks the same question without the array.
  // The draft below still reads `results` — it needs the rows themselves, not how many.
  const hasExisting = tournament.entries > 0;
  const [rows, setRows] = useState<ResultDraft[]>(() =>
    hasExisting ? draftsFromTournament(tournament) : [],
  );
  const [showProblems, setShowProblems] = useState(false);

  // The unclaimed place the search above is currently choosing a player for, or null when the
  // search adds a new row as usual. One search box serves both, so the operator reads a payout
  // sheet into one control whichever they are doing.
  const [assigning, setAssigning] = useState<string | null>(null);
  const assigningPlace = assigning
    ? rows.filter((r) => r.placed).findIndex((r) => r.key === assigning) + 1
    : 0;

  const update = (key: string, patch: Partial<ResultDraft>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const problems = resultProblems(rows);
  // An unclaimed place is not a player to create: it names nobody, and saving it does not card anyone.
  const creating = rows.filter((r) => r.playerId === null && !r.unclaimed).length;
  const paid = rows.filter((r) => r.placed).length;
  const addedIds = rows.map((r) => r.playerId).filter((id): id is string => id !== null);

  const save = () => {
    if (problems.length > 0) {
      setShowProblems(true);
      return;
    }
    onSave(rows);
  };

  return (
    <Modal
      title={`${hasExisting ? 'Update' : 'Enter'} results`}
      subtitle={`${tournament.name} · ${tournament.date} · $${tournament.buyIn.toLocaleString()}`}
      onClose={onClose}
      width="max-w-6xl"
      fill
      footer={
        <>
          <span className="mr-auto text-xs text-ink-800/60">
            {paid} paid · {rows.length} {rows.length === 1 ? 'entrant' : 'entrants'}
            {creating > 0 ? ` · ${creating} new ${creating === 1 ? 'player' : 'players'}` : ''}
          </span>
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving} className={btnPrimary}>
            {saving ? 'Saving…' : 'Save results'}
          </button>
        </>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        {hasExisting ? (
          <p className="rounded-lg border border-gold-500/40 bg-gold-500/10 p-3 text-xs text-ink-900">
            This tournament already has {tournament.entries} results. Saving replaces all of
            them with what is below.
          </p>
        ) : null}

        <ErrorNotice message={errorMessage} />

        {/* Outside the scrolling grid so its dropdown is not clipped by it. */}
        <div>
          {assigning ? (
            <p className="mb-1 flex items-center justify-between gap-3 rounded-lg border border-felt-500/40 bg-felt-50 px-3 py-2 text-xs text-ink-900">
              <span>
                Choose who finished in <strong>place {assigningPlace}</strong>, or add a new player
                with their own card. The card the file gave this place stays as a record and is not
                theirs.
              </span>
              <button type="button" onClick={() => setAssigning(null)} className="shrink-0 font-medium text-felt-700 hover:underline">
                Cancel
              </button>
            </p>
          ) : null}
          <PlayerPicker
            roomId={roomId}
            excludeIds={addedIds}
            onPick={(p) => {
              if (assigning) {
                setRows((rs) => assignExistingPlayer(rs, assigning, p));
                setAssigning(null);
              } else {
                setRows((rs) => [...rs, draftForPlayer(p)]);
              }
            }}
            onCreateNew={(term) => {
              if (assigning) {
                setRows((rs) => assignNewPlayer(rs, assigning, term));
                setAssigning(null);
              } else {
                setRows((rs) => [...rs, draftForNewPlayer(term)]);
              }
            }}
          />
          <p className="mt-1 text-[11px] text-ink-800/50">
Players are added in finishing order — use the arrows to move somebody up or down. Click a
            place number to mark that they entered but did not finish in the money.
          </p>
        </div>

        {showProblems && problems.length > 0 ? (
          <div className="max-h-32 overflow-auto rounded-lg border border-red-500/40 bg-red-500/5 p-3">
            <p className="mb-1 text-xs font-semibold text-red-700">
              {problems.length} {problems.length === 1 ? 'thing needs' : 'things need'} fixing
            </p>
            <ul className="space-y-0.5 text-xs text-red-700/90">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-ink-800/15">
          {rows.length === 0 ? (
            <p className="p-8 text-center text-sm text-ink-800/50">
Nobody added yet. Search above to add the first finisher.
            </p>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-felt-50/80 backdrop-blur">
                <tr className="text-left text-[11px] uppercase tracking-wide text-ink-800/60">
                  <th className="w-14 px-2 py-2 font-medium">Place</th>
                  <th className="min-w-44 px-2 py-2 font-medium">Name</th>
                  <th className="w-32 px-2 py-2 font-medium">Card ID</th>
                  <th className="min-w-40 px-2 py-2 font-medium">City / State</th>
                  <th className="w-28 px-2 py-2 font-medium">Prize</th>
                  <th className="w-20 px-2 py-2 font-medium">Buy-ins</th>
                  <th className="w-14 px-2 py-2 text-center font-medium">Deal</th>
                  <th className="min-w-48 px-2 py-2 font-medium">Prize notes</th>
                  <th className="w-20 px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <ResultRow
                    key={row.key}
                    row={row}
                    place={row.placed ? idx + 1 : null}
                    // A divider rather than a second table: one table keeps the columns lined up,
                    // and the boundary is exactly where the numbering stops.
                    startsField={!row.placed && (idx === 0 || rows[idx - 1]!.placed)}
                    isFirst={idx === 0}
                    isLast={idx === paid - 1}
                    onChange={(patch) => update(row.key, patch)}
                    onMove={(delta) => setRows((rs) => moveDraft(rs, idx, delta))}
                    onTogglePlaced={() => setRows((rs) => togglePlaced(rs, row.key))}
                    onRemove={() => setRows((rs) => rs.filter((r) => r.key !== row.key))}
                    onAssign={() => setAssigning(row.key)}
                    onReturnToUnclaimed={() => setRows((rs) => returnToUnclaimed(rs, row.key))}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Modal>
  );
}

const cell = 'w-full rounded border border-ink-800/15 px-2 py-1 text-sm outline-none focus:border-felt-500';
/** A matched player's own details, shown but not editable — there is no endpoint to change them. */
const echo = 'block truncate px-1 py-1 text-sm text-ink-800/70';
const move =
  'rounded p-0.5 text-ink-800/40 hover:bg-felt-50 hover:text-ink-950 disabled:opacity-25 disabled:hover:bg-transparent';

function ResultRow({
  row,
  place,
  startsField,
  isFirst,
  isLast,
  onChange,
  onMove,
  onTogglePlaced,
  onRemove,
  onAssign,
  onReturnToUnclaimed,
}: {
  row: ResultDraft;
  /** Null for an entrant who did not reach a paid place. */
  place: number | null;
  /** First row after the paid places, which is where the divider goes. */
  startsField: boolean;
  isFirst: boolean;
  isLast: boolean;
  onChange: (patch: Partial<ResultDraft>) => void;
  onMove: (delta: number) => void;
  onTogglePlaced: () => void;
  onRemove: () => void;
  /** Point the search at this unclaimed place, to choose who holds it. */
  onAssign: () => void;
  /** Take the player off this place, leaving it unclaimed. */
  onReturnToUnclaimed: () => void;
}) {
  // An unclaimed place is never a new player's row: `playerId === null` alone means "create one",
  // and an unclaimed place must not be read that way (ADR 0013).
  const isNew = row.playerId === null && !row.unclaimed;

  return (
    <tr
      className={`border-b border-ink-800/5 last:border-0 ${isNew ? 'bg-felt-50/30' : ''} ${
        startsField ? 'border-t-2 border-t-ink-800/20' : ''
      }`}
    >
      <td className="px-2 py-1.5 align-top">
        {place === null ? (
          <button
            type="button"
            onClick={onTogglePlaced}
            title="Move into the paid places"
            aria-label="Move into the paid places"
            className="py-1 text-xs text-ink-800/40 hover:text-felt-700"
          >
            —
          </button>
        ) : (
          <button
            type="button"
            onClick={onTogglePlaced}
            title="Did not reach a paid place"
            aria-label={`Place ${place} — click if they did not reach a paid place`}
            className="py-1 font-mono text-sm font-semibold text-ink-950 hover:text-felt-700"
          >
            {place}
          </button>
        )}
      </td>

      <td className="px-2 py-1.5 align-top">
        {row.unclaimed ? (
          <div>
            <span className={echo}>Unclaimed — place {place}</span>
            <button
              type="button"
              onClick={onAssign}
              className="mt-0.5 inline-flex items-center gap-1 px-1 text-[11px] font-medium text-felt-700 hover:underline"
            >
              <UserPlus className="size-3" /> Assign a player
            </button>
          </div>
        ) : isNew ? (
          <div className="flex gap-1">
            <input
              value={row.firstName}
              onChange={(e) => onChange({ firstName: e.target.value })}
              placeholder="First"
              aria-label="First name"
              className={cell}
            />
            <input
              value={row.lastName}
              onChange={(e) => onChange({ lastName: e.target.value })}
              placeholder="Last"
              aria-label="Last name"
              className={cell}
            />
          </div>
        ) : (
          <span className={echo}>
            {row.firstName} {row.lastName}
          </span>
        )}
        {isNew ? (
          <span className="mt-1 flex items-center gap-1 text-[10px] font-medium text-felt-700">
            <UserPlus className="size-2.5" /> New player
          </span>
        ) : null}
      </td>

      <td className="px-2 py-1.5 align-top">
        {row.unclaimed ? (
          // What the file said, not anybody's card: shown faint, and titled so.
          <span className={`${echo} font-mono text-xs text-ink-800/35`} title="What the file carried. It is not a player's card.">
            {row.sourceCardId ?? '—'}
          </span>
        ) : isNew ? (
          <input
            value={row.cardId}
            onChange={(e) => onChange({ cardId: e.target.value })}
            placeholder="Card ID"
            aria-label="Card ID"
            className={cell}
          />
        ) : (
          <span className={`${echo} font-mono text-xs`}>{row.cardId}</span>
        )}
      </td>

      <td className="px-2 py-1.5 align-top">
        {isNew ? (
          <div className="flex gap-1">
            <input
              value={row.city}
              onChange={(e) => onChange({ city: e.target.value })}
              placeholder="City"
              aria-label="City"
              className={cell}
            />
            <input
              value={row.state}
              onChange={(e) => onChange({ state: e.target.value })}
              placeholder="ST"
              aria-label="State"
              className="w-14 rounded border border-ink-800/15 px-2 py-1 text-sm outline-none focus:border-felt-500"
            />
          </div>
        ) : (
          <span className={echo}>
            {row.unclaimed ? '—' : [row.city, row.state].filter(Boolean).join(', ') || '—'}
          </span>
        )}
      </td>

      <td className="px-2 py-1.5 align-top">
        <input
          value={row.prize}
          onChange={(e) => onChange({ prize: e.target.value })}
          inputMode="numeric"
          placeholder="0"
          aria-label={`Prize won for place ${place}`}
          className={cell}
        />
      </td>

      <td className="px-2 py-1.5 align-top">
        <input
          value={row.buyins}
          onChange={(e) => onChange({ buyins: e.target.value })}
          inputMode="numeric"
          placeholder="—"
          aria-label={`Buy-ins for place ${place}`}
          title="Blank means the room does not track this, which is not the same as one."
          className={cell}
        />
      </td>

      <td className="px-2 py-1.5 text-center align-top">
        <input
          type="checkbox"
          checked={row.isInDeal}
          onChange={(e) => onChange({ isInDeal: e.target.checked })}
          aria-label={`Place ${place} was part of a deal`}
          className="mt-1.5 rounded"
        />
      </td>

      <td className="px-2 py-1.5 align-top">
        <input
          value={row.prizeNotes}
          onChange={(e) => onChange({ prizeNotes: e.target.value })}
          placeholder="e.g. three-handed deal, includes a seat"
          aria-label={`Prize notes for place ${place}`}
          className={cell}
        />
      </td>

      <td className="px-2 py-1.5 align-top">
        <div className="flex items-center gap-0.5 pt-0.5">
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={isFirst || place === null}
            aria-label={`Move place ${place} up`}
            className={move}
          >
            <ChevronUp className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={isLast || place === null}
            aria-label={`Move place ${place} down`}
            className={move}
          >
            <ChevronDown className="size-3.5" />
          </button>
          {place !== null && !row.unclaimed ? (
            <button
              type="button"
              onClick={onReturnToUnclaimed}
              title="Return this place to unclaimed"
              aria-label={`Return place ${place} to unclaimed`}
              className={move}
            >
              <UserX className="size-3.5" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove place ${place}`}
            className="rounded p-0.5 text-ink-800/40 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}
